import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/lib/i18n";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [state, setState] = useState({ status: "checking", data: null });
  const { t } = useLang();

  useEffect(() => {
    if (!sid) { setState({ status: "error", data: null }); return; }
    let attempts = 0;
    let cancelled = false;
    let timer = null;
    const poll = async () => {
      if (cancelled) return;
      attempts++;
      try {
        const r = await api.get(`/payments/status/${sid}`);
        if (cancelled) return;
        if (r.data.payment_status === "paid") { setState({ status: "paid", data: r.data }); return; }
        if (r.data.payment_status === "expired" || r.data.payment_status === "failed") {
          setState({ status: "failed", data: r.data }); return;
        }
        if (attempts >= 10) { setState({ status: "pending", data: r.data }); return; }
        timer = setTimeout(poll, 2000);
      } catch (e) {
        if (cancelled) return;
        console.error("payment poll failed", e);
        setState({ status: "error", data: null });
      }
    };
    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [sid]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-lg mx-auto px-6 py-20 text-center" data-testid="payment-success-page">
        {state.status === "checking" && (
          <><Loader2 className="w-12 h-12 text-primary mx-auto mb-6 animate-spin"/>
            <p className="text-muted-foreground">{t("pay.checking")}</p></>
        )}
        {state.status === "paid" && (
          <>
            <CheckCircle2 className="w-16 h-16 text-sage mx-auto mb-6"/>
            <h1 className="font-display text-5xl tracking-tight mb-4" data-testid="payment-success-title">{t("pay.success")}</h1>
            <p className="text-muted-foreground mb-4">{t("pay.success_body")}</p>
            {state.data && (
              <div className="bg-sand border border-border rounded-lg p-5 text-sm text-left mb-6">
                <div className="flex justify-between mb-1"><span>Camera:</span><b>{state.data.room_name}</b></div>
                <div className="flex justify-between mb-1"><span>Check-in:</span><b>{state.data.check_in}</b></div>
                <div className="flex justify-between mb-1"><span>Check-out:</span><b>{state.data.check_out}</b></div>
                <div className="flex justify-between text-primary font-display text-xl mt-2"><span>{t("dash.total")}:</span><b>€ {state.data.amount?.toFixed(2)}</b></div>
              </div>
            )}
            <Button asChild className="lift"><Link to="/">{t("pay.back_home")}</Link></Button>
          </>
        )}
        {(state.status === "pending" || state.status === "error" || state.status === "failed") && (
          <>
            <XCircle className="w-16 h-16 text-destructive mx-auto mb-6"/>
            <h1 className="font-display text-4xl mb-4">{t("pay.cancel")}</h1>
            <p className="text-muted-foreground mb-6">{t("pay.cancel_body")}</p>
            <Button asChild variant="outline" className="lift"><Link to="/">{t("pay.back_home")}</Link></Button>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const { t } = useLang();
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-lg mx-auto px-6 py-20 text-center" data-testid="payment-cancel-page">
        <XCircle className="w-16 h-16 text-destructive mx-auto mb-6"/>
        <h1 className="font-display text-5xl tracking-tight mb-4">{t("pay.cancel")}</h1>
        <p className="text-muted-foreground mb-6">{t("pay.cancel_body")}</p>
        <Button asChild className="lift"><Link to="/">{t("pay.back_home")}</Link></Button>
      </div>
    </div>
  );
}
